/**
 * API Sequence Agent — Generate Panel
 *
 * @module ui/components/node-config/ApiSequenceGeneratePanel
 *
 * Inline panel for AI-powered API sequence configuration.
 * Supports both prompt-based generation and generation from a completed
 * workflow run (feature-flagged).
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Text, TextField, Select, Flex } from '@chatsdet/ui';
import { Robot, ArrowClockwise } from '@phosphor-icons/react';
import type { ApiSequenceConfig } from '@chatsdet/types';
import { API_URL } from '../../../config';
import { useAuth } from '../../../contexts/AuthContext';
import { useApiSequenceGenerationStream } from '../../../hooks/useApiSequenceGenerationStream';
import { useWorkflowVariablesContext } from '../../pages/FlowDesigner/context/WorkflowVariablesContext';
import { ApiSequenceLogEntry } from './ApiSequenceLogEntry';
import { buildLogEntries } from './buildLogEntries';

type ApiSequenceGeneratePanelProps = {
  propertyId?: string;
  currentConfig: ApiSequenceConfig | null;
  credentialId?: string | null;
  onApplyConfig: (config: ApiSequenceConfig) => void;
};

type GenerateMode = 'prompt' | 'run';

type WorkflowListResponse = {
  ok: boolean;
  data: Array<{ id: string; name: string; status: string }>;
};

export function ApiSequenceGeneratePanel({
  propertyId,
  currentConfig,
  credentialId,
  onApplyConfig,
}: ApiSequenceGeneratePanelProps) {
  const { user } = useAuth();
  const runModeEnabled = user?.features?.apiSequenceGenerateFromRun === true;
  const workflowVariables = useWorkflowVariablesContext();

  const [mode, setMode] = React.useState<GenerateMode>('prompt');
  const [prompt, setPrompt] = React.useState('');
  const [selectedWorkflowId, setSelectedWorkflowId] = React.useState('');

  const generation = useApiSequenceGenerationStream();
  const { events, status, finalConfig, summary, cancel } = generation;
  const logContainerRef = React.useRef<HTMLDivElement>(null);

  const workflowsQuery = useQuery<WorkflowListResponse>({
    queryKey: ['api-sequence-generate-panel', 'workflows'],
    enabled: runModeEnabled && mode === 'run',
    queryFn: async () => {
      const response = await fetch(`${API_URL}/workflows?page=1&pageSize=100`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Failed to load workflows');
      }
      return response.json();
    },
  });

  const workflowOptions = React.useMemo(
    () => (workflowsQuery.data?.data ?? []).map((workflow) => ({ id: workflow.id, name: workflow.name })),
    [workflowsQuery.data]
  );

  React.useEffect(() => {
    if (!runModeEnabled) {
      setMode('prompt');
    }
  }, [runModeEnabled]);

  React.useEffect(() => {
    const element = logContainerRef.current;
    if (element) {
      element.scrollTop = element.scrollHeight;
    }
  }, [events]);

  const hasSteps = (currentConfig?.steps?.length ?? 0) > 0;

  const handleModeChange = (nextMode: GenerateMode) => {
    if (nextMode === mode) {
      return;
    }
    generation.reset();
    setMode(nextMode);
  };

  const handleGenerate = () => {
    if (mode === 'prompt') {
      if (!prompt.trim()) {
        return;
      }

      generation.startGeneration({
        endpoint: '/api-sequence-agent/generate',
        body: {
          prompt,
          propertyId,
          currentConfig,
          credentialId,
          workflowVariables: workflowVariables
            .filter(
              (v) =>
                v.source === 'credential' || v.source === 'seed' || v.source === 'childFlowOutput'
            )
            .map((v) => ({ name: v.name, source: v.source, value: v.designTimeValue ?? null })),
        },
      });
      return;
    }

    if (!selectedWorkflowId) {
      return;
    }

    generation.startGeneration({
      endpoint: '/api-sequence-agent/generate-from-run',
      body: {
        workflowId: selectedWorkflowId,
        workflowVariables: workflowVariables
          .filter(
            (v) =>
              v.source === 'credential' || v.source === 'seed' || v.source === 'childFlowOutput'
          )
          .map((v) => ({ name: v.name, source: v.source, value: v.designTimeValue ?? null })),
      },
    });
  };

  const handlePromptKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey && status !== 'running' && mode === 'prompt') {
      event.preventDefault();
      handleGenerate();
    }
  };

  const handleApply = () => {
    if (!finalConfig) {
      return;
    }
    onApplyConfig(finalConfig);
    generation.reset();
  };

  const logEntries = buildLogEntries(events, status);

  const disablePromptGenerate = !prompt.trim();
  const disableRunGenerate = !selectedWorkflowId || workflowsQuery.isLoading;

  const workflowSelectItems = [
    { id: '', label: 'Select a workflow...' },
    ...workflowOptions.map((w) => ({ id: w.id, label: w.name })),
  ];

  return (
    <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
      <Flex align="center" gap="sm">
        <Robot size={16} className="text-muted-foreground" />
        <Text size="sm" weight="medium">
          {hasSteps ? 'Regenerate with AI' : 'Generate with AI'}
        </Text>
      </Flex>

      {runModeEnabled && (
        <div className="inline-flex rounded-md border border-border bg-muted/50 p-0.5">
          <button
            type="button"
            className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
              mode === 'prompt'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => handleModeChange('prompt')}
          >
            From prompt
          </button>
          <button
            type="button"
            className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
              mode === 'run'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => handleModeChange('run')}
          >
            From workflow run
          </button>
        </div>
      )}

{mode === 'prompt' ? (
        <div className="flex gap-2 items-end" onKeyDown={handlePromptKeyDown}>
          <div className="flex-1">
            <TextField
              aria-label="Prompt"
              value={prompt}
              onChange={setPrompt}
              placeholder="What should this sequence do?"
              isDisabled={status === 'running'}
            />
          </div>
          {status === 'running' ? (
            <Button variant="outline" size="sm" onPress={cancel}>
              Cancel
            </Button>
          ) : (
            <Button variant="primary" size="sm" onPress={handleGenerate} isDisabled={disablePromptGenerate}>
              Generate
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <Select
            aria-label="Workflow"
            items={workflowSelectItems}
            selectedKey={selectedWorkflowId}
            onSelectionChange={(key) => setSelectedWorkflowId(String(key))}
            isDisabled={status === 'running' || workflowsQuery.isLoading}
          />

          {selectedWorkflowId && (
            <Text size="xs" className="text-muted-foreground">
              Uses the latest successful run with diagnostics for the selected workflow.
            </Text>
          )}

          <Flex justify="end">
            {status === 'running' ? (
              <Button variant="outline" size="sm" onPress={cancel}>
                Cancel
              </Button>
            ) : (
              <Button variant="primary" size="sm" onPress={handleGenerate} isDisabled={disableRunGenerate}>
                Generate
              </Button>
            )}
          </Flex>
        </div>
      )}

      {logEntries.length > 0 && (
        <div
          ref={logContainerRef}
          className="max-h-60 overflow-y-auto rounded-md border border-border bg-background py-1"
        >
          {logEntries.map((entry) => (
            <ApiSequenceLogEntry
              key={entry.key}
              icon={entry.icon}
              text={entry.text}
              detail={entry.detail}
              badge={entry.badge}
            />
          ))}
        </div>
      )}

      {status === 'complete' && finalConfig && (
        <div className="rounded-md border border-green-500/30 bg-green-500/5 p-3 space-y-2">
          <Text size="sm" weight="medium">
            {summary ??
              `${finalConfig.steps.length} API call${finalConfig.steps.length !== 1 ? 's' : ''} configured and tested`}
          </Text>
          <Flex gap="sm">
            <Button variant="primary" size="sm" onPress={handleApply}>
              Apply Configuration
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onPress={() => generation.reset()}
              iconLeft={<ArrowClockwise size={14} />}
            >
              Try again
            </Button>
          </Flex>
        </div>
      )}

      {status === 'error' && (
        <div className="rounded-md border border-red-500/30 bg-red-500/5 p-3">
          <Flex align="center" justify="between">
            <Text size="sm" className="text-red-400">
              Generation failed
            </Text>
            <Button
              variant="ghost"
              size="sm"
              onPress={() => generation.reset()}
              iconLeft={<ArrowClockwise size={14} />}
            >
              Try again
            </Button>
          </Flex>
        </div>
      )}
    </div>
  );
}
